import { useState, useEffect, useRef } from 'react'
import type { ChangeEvent } from 'react'
import { SchoolService, type School } from '../../services/schools'
import { notifySchoolLogoUpdated } from '../../utils/adminSchoolContext'
import { resolveStoragePublicUrl } from '../../utils/storageUrl'
import {
  AI_RESPONSE_POLICY_DEFAULT,
  normalizeAiResponsePolicy,
  type AiResponsePolicy,
} from '../../constants/aiResponsePolicy'
import { useAiResponsePolicyOptions } from '../../hooks/useAiResponsePolicyOptions'
import TeacherManager from '../TeacherManager'
import StudentManager from '../StudentManager'
import SchoolVideoManager from './SchoolVideoManager'
import SchoolFeaturedVideoManager from './SchoolFeaturedVideoManager'
import SchoolAnnouncementManager from './SchoolAnnouncementManager'

type SubTab = 'school-info' | 'teacher-management' | 'student-management' | 'content-management' | 'featured-groups' | 'announcements'

const subTabs: { key: SubTab; label: string }[] = [
  { key: 'school-info', label: '学校管理' },
  { key: 'teacher-management', label: '先生管理' },
  { key: 'student-management', label: '生徒管理' },
  { key: 'content-management', label: 'コンテンツ管理' },
  { key: 'featured-groups', label: 'カテゴリ管理' },
  { key: 'announcements', label: 'お知らせ管理' },
]

type LogoDisplayMode = 'both' | 'school_only' | 'system'

// 'school_only' stays admin-only: teachers can only pick between MiraPASS alone
// and the school logo alongside it.
const teacherLogoDisplayModes: { value: Exclude<LogoDisplayMode, 'school_only'>; label: string }[] = [
  { value: 'system', label: 'MiraPASSのみ' },
  { value: 'both', label: '学校ロゴ×MiraPASS' },
]

interface SchoolSettingsTabContentProps {
  schoolId: string
}

const SchoolSettingsTabContent = ({ schoolId }: SchoolSettingsTabContentProps) => {
  const [subTab, setSubTab] = useState<SubTab>('school-info')
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string>('')
  const [error, setError] = useState<string>('')

  // School basic info
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [representative, setRepresentative] = useState('')
  const [phone, setPhone] = useState('')
  const [contactEmail, setContactEmail] = useState('')

  // New AI settings
  const [aiEducationPolicy, setAiEducationPolicy] = useState('')
  const [aiResponsePolicy, setAiResponsePolicy] = useState<AiResponsePolicy>(AI_RESPONSE_POLICY_DEFAULT)
  const { options: aiResponsePolicyOptions } = useAiResponsePolicyOptions()

  // Logo
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [logoDisplayMode, setLogoDisplayMode] = useState<LogoDisplayMode>('both')
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const previewObjectUrl = useRef<string | null>(null)

  useEffect(() => {
    loadSchool()
  }, [schoolId])

  useEffect(() => {
    return () => {
      if (previewObjectUrl.current) {
        URL.revokeObjectURL(previewObjectUrl.current)
      }
    }
  }, [])

  const loadSchool = async () => {
    try {
      setIsLoading(true)
      setError('')
      const school: School | null = await SchoolService.getSchool(schoolId)
      if (school) {
        setName(school.name || '')
        setAddress(school.address || '')
        setRepresentative(school.representative || '')
        setPhone(school.phone || '')
        setContactEmail(school.contact_email || '')
        setAiEducationPolicy(school.ai_education_policy || '')
        setAiResponsePolicy(normalizeAiResponsePolicy(school.ai_response_policy))
        setLogoUrl(school.logo_url || null)
        setLogoDisplayMode(school.logo_display_mode || 'both')
      }
    } catch (err) {
      console.error('Failed to load school:', err)
      setError('学校情報の取得に失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setSaveMessage('画像ファイルを選択してください')
      setTimeout(() => setSaveMessage(''), 4000)
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setSaveMessage('ファイルサイズは5MB以下にしてください')
      setTimeout(() => setSaveMessage(''), 4000)
      return
    }

    if (previewObjectUrl.current) {
      URL.revokeObjectURL(previewObjectUrl.current)
    }

    const objectUrl = URL.createObjectURL(file)
    previewObjectUrl.current = objectUrl
    setLogoFile(file)
    setLogoPreview(objectUrl)
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setSaveMessage('学校名は必須です')
      setTimeout(() => setSaveMessage(''), 4000)
      return
    }

    setIsSaving(true)
    setSaveMessage('')

    try {
      let resolvedLogoUrl = logoUrl

      if (logoFile) {
        resolvedLogoUrl = await SchoolService.uploadSchoolLogo(schoolId, logoFile)
        setLogoUrl(resolvedLogoUrl)
        setLogoFile(null)
        if (previewObjectUrl.current) {
          URL.revokeObjectURL(previewObjectUrl.current)
          previewObjectUrl.current = null
        }
        setLogoPreview(null)
      }

      await SchoolService.updateSchoolAsTeacher(schoolId, {
        name: name.trim(),
        address: address.trim() || null,
        representative: representative.trim() || null,
        phone: phone.trim() || null,
        contact_email: contactEmail.trim() || null,
        ai_education_policy: aiEducationPolicy.trim() || null,
        ai_response_policy: aiResponsePolicy,
        logo_url: resolvedLogoUrl,
        logo_display_mode: logoDisplayMode,
      })

      notifySchoolLogoUpdated(schoolId, resolvedLogoUrl, logoDisplayMode)

      setSaveMessage('学校設定を保存しました')
      setTimeout(() => setSaveMessage(''), 4000)
    } catch (err) {
      console.error('Failed to save school settings:', err instanceof Error ? err.message : JSON.stringify(err))
      setSaveMessage('保存に失敗しました。もう一度お試しください')
      setTimeout(() => setSaveMessage(''), 5000)
    } finally {
      setIsSaving(false)
    }
  }

  const displayLogoSrc = logoPreview || (logoUrl && resolveStoragePublicUrl(logoUrl))

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-700">{error}</p>
        <button
          onClick={loadSchool}
          className="mt-3 text-sm text-red-600 underline hover:no-underline"
        >
          再読み込み
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Sub-tab navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {subTabs.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSubTab(key)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                subTab === key
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {subTab === 'teacher-management' && <TeacherManager schoolId={schoolId} />}
      {subTab === 'student-management' && <StudentManager schoolId={schoolId} />}
      {subTab === 'content-management' && <SchoolVideoManager schoolId={schoolId} />}
      {subTab === 'featured-groups' && <SchoolFeaturedVideoManager schoolId={schoolId} />}
      {subTab === 'announcements' && <SchoolAnnouncementManager schoolId={schoolId} />}

      {subTab === 'school-info' && <>

      {/* School basic info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">学校情報</h3>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              学校名 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">住所</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">代表者</label>
            <input
              type="text"
              value={representative}
              onChange={(e) => setRepresentative(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">電話番号</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">メールアドレス</label>
            <input
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Logo */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">学校のロゴ</h3>
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            {displayLogoSrc ? (
              <img
                src={displayLogoSrc}
                alt="学校のロゴ"
                className="w-32 h-32 object-contain border border-gray-200 rounded-lg bg-gray-50"
              />
            ) : (
              <div className="w-32 h-32 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-500 mb-3">
              PNG、JPG、GIF（最大5MB）
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors"
            >
              ファイルアップロード
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
            />
            {logoFile && (
              <p className="mt-2 text-sm text-gray-600">{logoFile.name}</p>
            )}
            <div className="mt-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">ロゴの表示設定</label>
              <select
                value={logoDisplayMode}
                onChange={(e) => setLogoDisplayMode(e.target.value as LogoDisplayMode)}
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {teacherLogoDisplayModes.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
                {/* Keeps an admin-set 'school_only' visible (and intact on save)
                    without offering it as a choice. */}
                {logoDisplayMode === 'school_only' && (
                  <option value="school_only" disabled>学校ロゴのみ（管理者設定）</option>
                )}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* AI Education Policy */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">AIへ反映させる教育方針</h3>
        <p className="text-sm text-gray-500 mb-4">ここに入力した内容がAIのシステム設定に反映されます</p>
        <textarea
          value={aiEducationPolicy}
          onChange={(e) => setAiEducationPolicy(e.target.value)}
          rows={5}
          placeholder="例：&#10;・主体性を育む&#10;・一人一人の夢の実現&#10;・仲間との絆"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-y text-sm"
        />
      </div>

      {/* AI Response Policy */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">AIの回答方針</h3>
        <p className="text-sm text-gray-500 mb-4">AIがどのようなスタンスで回答するかを設定します</p>
        <div className="space-y-3">
          {aiResponsePolicyOptions.map((option) => (
            <label key={option.value} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="radio"
                name="aiResponsePolicy"
                value={option.value}
                checked={aiResponsePolicy === option.value}
                onChange={() => setAiResponsePolicy(option.value)}
                className="mt-0.5 text-purple-600 focus:ring-purple-500"
              />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Save button + message */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`px-6 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
            isSaving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'text-white bg-purple-600 hover:bg-purple-700'
          }`}
        >
          {isSaving ? '保存中...' : '変更する'}
        </button>

        {saveMessage && (
          <span className={`text-sm font-medium ${
            saveMessage.includes('失敗') || saveMessage.includes('選択') || saveMessage.includes('以下') || saveMessage.includes('必須')
              ? 'text-red-600'
              : 'text-green-600'
          }`}>
            {saveMessage}
          </span>
        )}
      </div>

      </>}
    </div>
  )
}

export default SchoolSettingsTabContent
